import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useContentSetting } from "./ContentSetting";
import { tune_file, ended_call_tune } from "../assets";
import Peer from "peerjs";
import { useSnackbar } from "notistack";
import {
  CONSTRAINTS,
  ERROR_MESSAGES,
  getStreamStatus,
  GetVideoStream,
  handleShowNotification,
  isPeerConnectionReady,
  PEER_CONFIG,
  createPeerLifecycle,
} from "../utils/classRoomUtils";
import { isOnlineSchoolCapability } from "../utils/associateCapabilities";
import ClassRoomProviderChildern from "../components/ClassRoom/ClassRoomProviderChildern";
import logger from "../utils/logger";

const ClassRoomContext = createContext();
export const useClassRoom = () => useContext(ClassRoomContext);

export function ClassRoomProvider({ children }) {
  // Refs
  const isDisconnectingRef = useRef(false);
  const localStreamRef = useRef(null);
  const startedCallRef = useRef(null);
  const peerInstanceRef = useRef(null);
  const ensurePeerReadyRef = useRef(null);
  const peerLifecycleRef = useRef(null);
  const attachMediaCallHandlersRef = useRef(null);
  const handleStopAudioRef = useRef(null);
  const socketRef = useRef(null);
  const userInfoRef = useRef(null);

  // Hooks
  const { enqueueSnackbar } = useSnackbar();
  const { socket, userInfo, startedMeeting } = useContentSetting();
  const [callStatus, setCallStatus] = useState("");
  socketRef.current = socket;
  userInfoRef.current = userInfo;

  const [localUser, setLocalUser] = useState({
    isMuted: false,
    stream: null,
    peer: null,
    peerId: "",
  });

  const [remoteUser, setRemoteUser] = useState({
    isMuted: false,
    stream: null,
    peer: null,
    peerId: "",
  });

  // State
  const [state, setState] = useState({
    remoteStreams: [],
    lastCallID: null,
    callUser: null,
    selectedUser: {},
    isUnavailable: false,
    isSharedScreen: false,
    isMicAccessible: false,
    isPaused: true,
    isDisconnecting: false,
    isPeerReconnecting: false,
  });

  // Audio objects
  const [startCallTune] = useState(new Audio(tune_file));
  const [endedCallTune] = useState(new Audio(ended_call_tune));

  // Derived state
  const {
    remoteStreams,
    lastCallID,
    callUser,
    selectedUser,
    isUnavailable,
    isSharedScreen,
    isMicAccessible,
    isPaused,
    isDisconnecting,
    isPeerReconnecting,
  } = state;
  const isOnlineSchoolEnabled = isOnlineSchoolCapability(userInfo);

  const updateState = (newState) => {
    setState((prev) => ({ ...prev, ...newState }));
  };

  const updateLocalUser = (newState) => {
    setLocalUser((prev) => ({ ...prev, ...newState }));
  };

  const updateRemoteUser = (newState) => {
    setRemoteUser((prev) => ({ ...prev, ...newState }));
  };

  // Helper to add/update remote stream
  const updateRemoteStream = (peerId, stream) => {
    setState((prev) => {
      const existingIndex = prev.remoteStreams.findIndex(
        (s) => s.peerId === peerId
      );
      if (existingIndex >= 0) {
        const updated = [...prev.remoteStreams];
        updated[existingIndex] = { peerId, stream };
        return { ...prev, remoteStreams: updated };
      }
      return {
        ...prev,
        remoteStreams: [...prev.remoteStreams, { peerId, stream }],
      };
    });
  };

  // Media handling
  const handleGetStream = useCallback(async (isScreenShare = false) => {
    try {
      // Check microphone permission
      const micPermission = await navigator.permissions.query({
        name: "microphone",
      });
      if (micPermission.state === "denied") {
        alert(ERROR_MESSAGES.mic);
        return false;
      }

      // Get audio stream
      let stream = await navigator.mediaDevices.getUserMedia(CONSTRAINTS);

      const { isMuted } = getStreamStatus(localStreamRef.current);
      // Handle mute state
      if (isMuted) {
        stream.getAudioTracks().forEach((track) => (track.enabled = false));
      }

      // Handle screen sharing
      if (isScreenShare) {
        const cameraPermission = await navigator.permissions.query({
          name: "camera",
        });
        if (cameraPermission.state === "denied") {
          alert(ERROR_MESSAGES.camera);
          return null;
        }

        try {
          const screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
          });
          const videoTrack = screenStream.getVideoTracks()[0];
          stream.addTrack(videoTrack);

          videoTrack.onended = () => {
            stream.getTracks().forEach((track) => track.stop());
            toggleScreenShare(false);
          };
        } catch (error) {
          return null;
        }
      }

      // Clean up previous stream
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      localStreamRef.current = stream;
      updateLocalUser({ stream });
      return stream;
    } catch (error) {
      logger.error("Error getting media stream:", error);
      return null;
    }
  }, []);

  const acceptCall = async () => {
    setCallStatus("accepting");
    let stream = await handleGetStream();
    if (!stream) {
      enqueueSnackbar(ERROR_MESSAGES.mic, { variant: "error" });
      socket.emit("permissions_error", {
        to: callUser,
        from: userInfo._id,
        last_call_id: lastCallID,
      });
      return;
    }

    handlePeerCall(stream, true);
  };

  const resolveCallMessageId = (message) =>
    message?._id || message?.id || null;

  const handleStopAudio = useCallback(() => {
    startCallTune.pause();
    startCallTune.currentTime = 0;
  }, [startCallTune]);

  const isCleaningUpRef = useRef(false);

  const cleanupCallLocally = useCallback(
    ({ unavailable = false, playEndedTune = true } = {}) => {
      if (isCleaningUpRef.current) return;
      isCleaningUpRef.current = true;

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }
      updateLocalUser({ stream: null, isMuted: false });
      updateRemoteUser({ stream: null, peerId: "", isMuted: false });

      const activeCall = startedCallRef.current;
      startedCallRef.current = null;
      try {
        activeCall?.close();
      } catch (e) {
        logger.error("Error closing media call", e);
      }
      isDisconnectingRef.current = false;

      updateState({
        remoteStreams: [],
        lastCallID: null,
        callUser: null,
        isPaused: true,
        isScreenSharing: false,
        isSharedScreen: false,
        isDisconnecting: false,
        ...(unavailable ? { isUnavailable: true } : {}),
      });
      setCallStatus("");
      handleStopAudio();
      if (playEndedTune) {
        endedCallTune.play().catch(() => {});
      }
      isCleaningUpRef.current = false;
      peerLifecycleRef.current?.notifyCallEnded();
    },
    [endedCallTune, handleStopAudio]
  );

  // Call management
  const handleStartCall = useCallback(
    async (conversationId, user) => {
      let peerId = localUser.peerId;
      try {
        if (ensurePeerReadyRef.current) {
          peerId = await ensurePeerReadyRef.current();
        }
      } catch (err) {
        logger.warn("Peer not ready before start call", err);
        enqueueSnackbar(
          "Call service is reconnecting. Please try again in a moment.",
          { variant: "warning" }
        );
        return;
      }

      if (!peerId) {
        enqueueSnackbar(
          "Call service is still connecting. Please try again in a moment.",
          { variant: "warning" }
        );
        return;
      }

      const stream = await handleGetStream();
      if (!stream) return;

      updateState({
        selectedUser: user,
        callUser: user._id,
      });

      socket.emit("user:call", {
        to: user._id,
        conversation_id: conversationId,
        type: userInfo.user_type,
        sender_id: userInfo._id,
        peer_id: peerId,
      });
    },
    [enqueueSnackbar, handleGetStream, localUser.peerId, socket, userInfo]
  );

  const attachMediaCallHandlers = useCallback(
    (mediaCall) => {
      mediaCall.on("error", (err) => {
        logger.error("MediaConnection error:", err);
        enqueueSnackbar(
          "Call connection failed. Check your network or try again.",
          { variant: "error" }
        );
      });
      mediaCall.on("close", () => {
        // Remote hung up via PeerJS — tear down even if socket end event was missed.
        cleanupCallLocally({ playEndedTune: true });
      });
      const pc = mediaCall.peerConnection;
      if (!pc) return;
      const onIce = () => {
        if (pc.iceConnectionState === "failed") {
          enqueueSnackbar(
            "Network connection failed. If this keeps happening, ensure TURN (UDP/TCP) is allowed.",
            { variant: "error" }
          );
          pc.removeEventListener("iceconnectionstatechange", onIce);
        }
      };
      pc.addEventListener("iceconnectionstatechange", onIce);
    },
    [cleanupCallLocally, enqueueSnackbar]
  );

  const handleIncomingCall = useCallback(async (data) => {
    let { from, last_message, peer_id } = data;
    const callId = resolveCallMessageId(last_message);
    let getStream = await handleGetStream();
    if (!getStream) {
      enqueueSnackbar(ERROR_MESSAGES.mic, { variant: "error" });
      socket.emit("permissions_error", {
        to: from,
        from: userInfo._id,
        last_call_id: callId,
      });
      return;
    }
    getStream.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;
    updateLocalUser({ stream: null });
    startCallTune.play();
    startCallTune.addEventListener("ended", () => {
      startCallTune.play();
    });
    setCallStatus("incoming");
    updateState({
      lastCallID: callId,
      callUser: from,
    });
    updateRemoteUser({ peerId: peer_id });
    socket.emit("ringing:call", {
      to: from,
      from: userInfo._id,
      last_call_id: callId,
    });
  }, [
    handleGetStream,
    enqueueSnackbar,
    socket,
    userInfo,
    startCallTune,
    updateState,
    updateRemoteUser,
    updateLocalUser,
  ]);

  const handleCallRinging = useCallback(
    ({ data } = {}) => {
      if (data?.to === userInfo._id) {
        updateState({ lastCallID: data.last_call_id });

        startCallTune.play();
        startCallTune.addEventListener("ended", () => startCallTune.play());
      }
    },
    [startCallTune, userInfo]
  );

  const handleCallAccepted = useCallback(
    ({ data } = {}) => {
      if (data?.to === userInfo._id) {
        updateRemoteUser({ peerId: data.peer_id });
        handleStopAudio();
      }
    },
    [userInfo, handleStopAudio]
  );

  const handleChangeMedia = useCallback(
    (data) => {
      if (data.to !== userInfo._id) return;
      const { media_status, media_type } = data;
      if (media_type === "is_paused") {
        updateState({ isPaused: media_status });
      } else if (media_type === "is_muted") {
        updateRemoteUser({ isMuted: media_status });
      } else if (media_type === "is_screen_shared") {
        updateState({ isSharedScreen: media_status });
      }
    },
    [userInfo]
  );

  const handleRejoin = useCallback(async () => {
    if (!startedMeeting) return;

    const { participants, _id } = startedMeeting;
    const user = participants.find((p) => p.member !== userInfo._id);
    const iAm = participants.find((p) => p.member === userInfo._id);

    if (user && iAm) {
      const { member, is_paused, is_screen_shared } = user;
      const { is_muted } = iAm;

      const stream = await handleGetStream(is_screen_shared);
      if (!stream) return;

      if (is_muted) {
        stream.getAudioTracks().forEach((track) => (track.enabled = false));
      }
      updateLocalUser({ isMuted: is_muted });
      const updates = {
        callUser: member,
        lastCallID: _id,
        isPaused: !is_paused,
        isMuted: user.is_muted,
      };

      updateState(updates);

      let peerId = localUser.peerId;
      try {
        if (ensurePeerReadyRef.current) {
          peerId = await ensurePeerReadyRef.current();
        }
      } catch (err) {
        logger.warn("Peer not ready before rejoin", err);
        enqueueSnackbar(
          "Call service is reconnecting. Please try again in a moment.",
          { variant: "warning" }
        );
        return;
      }

      socket.emit("user:rejoined", {
        to: member,
        from: userInfo._id,
        peer_id: peerId,
        last_call_id: _id,
      });
    }
  }, [
    enqueueSnackbar,
    handleGetStream,
    localUser.peerId,
    socket,
    startedMeeting,
    userInfo,
  ]);

  const handleEndCall = useCallback(() => {
    socket.emit("call:ended", {
      to: callUser,
      from: userInfo._id,
      last_call_id: lastCallID,
    });
    cleanupCallLocally({ playEndedTune: true });
  }, [callUser, cleanupCallLocally, lastCallID, socket, userInfo]);

  const handlePeerCall = useCallback(
    async (stream, is_accepted) => {
      let peer = localUser.peer;
      try {
        if (ensurePeerReadyRef.current) {
          await ensurePeerReadyRef.current();
          peer = peerInstanceRef.current || localUser.peer;
        }
      } catch (err) {
        logger.warn("Peer not ready before media call", err);
        enqueueSnackbar(
          "Call service is reconnecting. Please try again in a moment.",
          { variant: "warning" }
        );
        return;
      }

      if (!isPeerConnectionReady(peer) || !remoteUser.peerId) {
        logger.error("Peer call aborted: missing peer or remote peer id", {
          hasPeer: !!peer,
          remotePeerId: remoteUser.peerId,
        });
        return;
      }

      const call = await peer.call(remoteUser.peerId, stream);
      startedCallRef.current = call;
      if (call) attachMediaCallHandlers(call);

      if (call && is_accepted) {
        const acceptData = {
          to: callUser,
          from: userInfo._id,
          peer_id: peer.id || localUser.peerId,
          type: userInfo.user_type,
          last_call_id: lastCallID,
        };
        socket.emit("call:accepted", acceptData);
      }

      if (!call) {
        enqueueSnackbar("Could not connect the call. Please try again.", {
          variant: "error",
        });
        return;
      }

      call.on("stream", (remoteStream) => {
        updateRemoteStream(remoteUser.peerId, remoteStream);
        handleStopAudio();

        if (isDisconnectingRef.current) {
          enqueueSnackbar("Connection has been restored", {
            variant: "info",
          });
        }

        isDisconnectingRef.current = false;
        updateState({ isDisconnecting: false });
        setCallStatus("accepted");
      });
    },
    [
      localUser.peer,
      remoteUser.peerId,
      handleStopAudio,
      enqueueSnackbar,
      callUser,
      lastCallID,
      localUser.peerId,
      socket,
      userInfo,
      attachMediaCallHandlers,
    ]
  );

  const toggleScreenShare = useCallback(
    async (enableScreenShare) => {
      try {
        const savedCallUser = callUser;
        const savedLastCallID = lastCallID;

        const currentStream = localStreamRef.current;
        const audioTracks = currentStream?.getAudioTracks() || [];
        const cameraVideoTracks =
          currentStream
            ?.getVideoTracks()
            .filter((t) => !t.label.includes("screen")) || [];

        const screenTracks =
          currentStream
            ?.getVideoTracks()
            .filter((t) => t.label.includes("screen")) || [];
        screenTracks.forEach((track) => {
          track.stop();
          currentStream?.removeTrack(track);
        });

        if (enableScreenShare) {
          const screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: true,
          });

          const newScreenTrack = screenStream.getVideoTracks()[0];

          const newStream = new MediaStream([
            ...audioTracks,
            newScreenTrack,
            ...cameraVideoTracks,
          ]);

          newScreenTrack.onended = () => {
            toggleScreenShare(false);
          };

          localStreamRef.current = newStream;
          updateLocalUser({ stream: newStream });
          await handlePeerCall(newStream);
        } else {
          let newStream = new MediaStream([...audioTracks]);

          if (cameraVideoTracks.length === 0 && !isPaused) {
            try {
              const cameraStream = await navigator.mediaDevices.getUserMedia({
                video: true,
              });
              cameraVideoTracks.push(...cameraStream.getVideoTracks());
            } catch (error) {
              logger.error("Couldn't restore camera", error);
            }
          }

          if (cameraVideoTracks.length > 0) {
            newStream.addTrack(cameraVideoTracks[0]);
          }

          localStreamRef.current = newStream;
          updateLocalUser({ stream: newStream });
          await handlePeerCall(newStream);
        }

        updateState({ isSharedScreen: enableScreenShare });

        socket.emit("changed_media", {
          to: savedCallUser,
          from: userInfo._id,
          last_call_id: savedLastCallID,
          media_type: "is_screen_shared",
          media_status: enableScreenShare,
          peer_id: localUser.peerId,
        });
      } catch (error) {
        logger.error("Screen share toggle failed:", error);
        enqueueSnackbar("Failed to toggle screen sharing", {
          variant: "error",
        });
      }
    },
    [
      callUser,
      lastCallID,
      localUser.peerId,
      socket,
      userInfo,
      handlePeerCall,
      isPaused,
      enqueueSnackbar,
    ]
  );

  const handleStopCall = useCallback(
    (payload) => {
      const data = payload?.data ?? payload ?? {};
      const incomingCallId = data.last_call_id;
      const idMatches =
        Boolean(incomingCallId) &&
        Boolean(lastCallID) &&
        String(incomingCallId) === String(lastCallID);
      const isDirectedAtMe =
        data.to === userInfo._id ||
        data.from === userInfo._id ||
        data.event === "call:unavailable";

      if (idMatches || isDirectedAtMe) {
        cleanupCallLocally({
          unavailable: data.event === "call:unavailable",
          playEndedTune: true,
        });
      }
    },
    [cleanupCallLocally, lastCallID, userInfo]
  );

  const handleAccessMic = async () => {
    const stream = await handleGetStream();
    if (stream) {
      updateState({ isMicAccessible: true });
      stream.getTracks().forEach((track) => track.stop());
    }
  };

  const handleToggleCam = async () => {
    let stream = null;
    let error = "";
    const { isMuted } = localUser;

    if (isPaused) {
      stream = await GetVideoStream(localUser.stream);
      if (!stream) {
        enqueueSnackbar(error, { variant: "error" });
        return;
      }
      if (isMuted) {
        stream.getAudioTracks().forEach((track) => (track.enabled = false));
      }
      const videoTracks = stream.getVideoTracks();
      videoTracks.forEach((track) => (track.enabled = isPaused));
      updateLocalUser({ stream });
      localStreamRef.current = stream;
      updateState({ isPaused: false });
      handlePeerCall(stream);
    } else {
      const videoTracks = localUser.stream.getVideoTracks();
      videoTracks.forEach((track) => (track.enabled = isPaused));
      updateState({ isPaused: true });
    }

    socket.emit("changed_media", {
      to: callUser,
      from: userInfo._id,
      last_call_id: lastCallID,
      media_type: "is_paused",
      media_status: !isPaused,
    });
  };

  // Keep latest handlers available to PeerJS lifecycle without rebinding on every render.
  attachMediaCallHandlersRef.current = attachMediaCallHandlers;
  handleStopAudioRef.current = handleStopAudio;

  // PeerJS lifecycle (reconnect mid-call; recreate only when idle).
  useEffect(() => {
    if (!isOnlineSchoolEnabled) {
      updateState({ isMicAccessible: false, isPeerReconnecting: false });
      updateLocalUser({ peer: null, peerId: "" });
      peerInstanceRef.current = null;
      ensurePeerReadyRef.current = null;
      peerLifecycleRef.current = null;
      return;
    }

    handleAccessMic();

    const lifecycle = createPeerLifecycle({
      Peer,
      peerConfig: PEER_CONFIG,
      getActiveMediaCall: () => startedCallRef.current,
      onStateChange: ({ peer, peerId, status }) => {
        peerInstanceRef.current = peer;
        updateLocalUser({
          peer,
          peerId: status === "open" ? peerId : peer?.id || peerId || "",
        });
        updateState({
          isPeerReconnecting: status === "reconnecting",
        });
      },
      onIncomingCall: (call) => {
        attachMediaCallHandlersRef.current?.(call);
        const stream = localStreamRef.current;
        if (!stream) {
          logger.error("Incoming peer call with no local stream yet");
          enqueueSnackbar(
            "Microphone not ready. Please allow mic access and try again.",
            { variant: "error" }
          );
          return;
        }
        startedCallRef.current = call;
        call.answer(stream);
        call.on("stream", (remoteStream) => {
          updateRemoteStream(call.peer, remoteStream);
          handleStopAudioRef.current?.();

          if (isDisconnectingRef.current) {
            enqueueSnackbar("Connection has been restored", {
              variant: "info",
            });
          }

          isDisconnectingRef.current = false;
          updateState({ isDisconnecting: false });
          setCallStatus("accepted");
        });
      },
      onError: (err) => {
        socketRef.current?.emit("activity-log", {
          user_info: userInfoRef.current,
          info_obj: err,
        });
      },
      logWarn: (msg, meta) => logger.warn(msg, meta),
      logInfo: (msg, meta) => logger.info(msg, meta),
      logError: (msg, meta) => logger.error(msg, meta),
    });

    peerLifecycleRef.current = lifecycle;
    ensurePeerReadyRef.current = () => lifecycle.ensureReady();

    return () => {
      lifecycle.destroy();
      peerLifecycleRef.current = null;
      ensurePeerReadyRef.current = null;
      peerInstanceRef.current = null;
      updateLocalUser({ peer: null, peerId: "" });
      updateState({ isPeerReconnecting: false });
    };
  }, [isOnlineSchoolEnabled, enqueueSnackbar]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const socketEvents = {
      "incoming:call": handleIncomingCall,
      "call:ringing": handleCallRinging,
      "call:accepted": handleCallAccepted,
      "call:declined": handleStopCall,
      "call:ended": handleStopCall,
      "call:no_answered": handleStopCall,
      "call:canceled": handleStopCall,
      permissions_error: handleStopCall,
      "call:disconnected": handleStopCall,
      "call:unavailable": (data) => handleStopCall({ data }),
      changed_media: handleChangeMedia,
    };

    Object.entries(socketEvents).forEach(([event, handler]) => {
      socket.on(event, handler);
    });

    return () => {
      Object.entries(socketEvents).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
    };
  }, [
    socket,
    handleIncomingCall,
    handleCallRinging,
    handleCallAccepted,
    handleStopCall,
    handleChangeMedia,
  ]);

  // Disconnection handling
  useEffect(() => {
    if (!socket) return;

    const handleDisconnecting = (data) => {
      if (data.last_call_id === lastCallID) {
        isDisconnectingRef.current = true;
        updateState({ isDisconnecting: true });
        const timeoutId = setTimeout(() => {
          if (isDisconnectingRef.current) {
            handleEndCall();
          }
        }, 30000);

        return () => clearTimeout(timeoutId);
      }
    };

    socket.on("call-disconnecting", handleDisconnecting);
    return () => {
      socket.off("call-disconnecting", handleDisconnecting);
    };
  }, [socket, lastCallID, handleEndCall]);

  // Context value
  const contextValue = {
    callUser,
    startCallTune,
    remoteStreams,
    lastCallID,
    localUser,
    remoteUser,
    isSharedScreen,
    selectedUser,
    handleStartCall,
    toggleScreenShare,
    isMicAccessible,
    handleEndCall,
    cleanupCallLocally,
    acceptCall,
    callStatus,
    handleToggleCam,
    updateLocalUser,
    isPaused,
    handleShowNotification,
  };

  return (
    <ClassRoomContext.Provider value={contextValue}>
      {children}
      <ClassRoomProviderChildern
        {...{
          isDisconnecting,
          isPeerReconnecting,
          updateState,
          isUnavailable,
          callUser,
        }}
      />
    </ClassRoomContext.Provider>
  );
}
